/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-19
 */

import { useMemo } from 'react';
import {
  useGetCurrentSubscriptionQuery,
  useGetSubscriptionUsageQuery,
} from '../features/auth/authApi';
import { SubscriptionLimits } from '../types/subscription';

/**
 * Hook for checking subscription limits and usage
 *
 * @returns {SubscriptionLimits & { isLoading: boolean, error: any }} Subscription limits and usage data
 *
 * @example
 * const { canCreateStudio, canAddStudioMember, remainingStudios, isAtStudioLimit } = useSubscriptionLimits();
 */
export const useSubscriptionLimits = (): SubscriptionLimits & {
  isLoading: boolean;
  error: any;
} => {
  const {
    data: subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = useGetCurrentSubscriptionQuery();

  const {
    data: usage,
    isLoading: usageLoading,
    error: usageError,
  } = useGetSubscriptionUsageQuery();

  const limits = useMemo((): SubscriptionLimits => {
    // Default to free plan limits if no subscription data
    if (!subscription || !usage) {
      return {
        canCreateStudio: false,
        canAddStudioMember: false,
        remainingStudios: 0,
        remainingUsers: 0,
        isAtStudioLimit: true,
        isAtUserLimit: true,
      };
    }

    const { plan } = subscription;
    const currentStudioCount = usage.usage?.owned_studios?.count || 0;

    // Check studio limits
    const maxStudios = plan.max_owned_studios;
    const isUnlimitedStudios = maxStudios === 0;
    const canCreateStudio =
      isUnlimitedStudios || currentStudioCount < maxStudios;
    const remainingStudios = isUnlimitedStudios
      ? Infinity
      : Math.max(0, maxStudios - currentStudioCount);
    const isAtStudioLimit =
      !isUnlimitedStudios && currentStudioCount >= maxStudios;

    // For user limits, we'll need to check per-studio when we have studioId
    // For now, return general user limit info
    const maxUsersPerStudio = plan.max_users_per_studio;
    const isUnlimitedUsers = maxUsersPerStudio === 0;

    return {
      canCreateStudio,
      canAddStudioMember: !isUnlimitedUsers, // Will be refined with studio-specific data
      remainingStudios: remainingStudios === Infinity ? 0 : remainingStudios,
      remainingUsers: isUnlimitedUsers ? 0 : maxUsersPerStudio, // Will be refined with studio-specific data
      isAtStudioLimit,
      isAtUserLimit: false, // Will be refined with studio-specific data
    };
  }, [subscription, usage]);

  return {
    ...limits,
    isLoading: subscriptionLoading || usageLoading,
    error: subscriptionError || usageError,
  };
};

/**
 * Hook for checking studio-specific user limits
 *
 * @param studioId - The ID of the studio to check limits for
 * @param currentUserCount - Current number of users in the studio
 * @returns Object with user limit information for the specific studio
 */
export const useStudioUserLimits = (
  studioId?: string,
  currentUserCount: number = 0
) => {
  const { data: subscription } = useGetCurrentSubscriptionQuery();

  return useMemo(() => {
    if (!subscription) {
      return {
        canAddUser: false,
        remainingUsers: 0,
        isAtUserLimit: true,
        userLimitPercent: 100,
      };
    }

    const { plan } = subscription;
    const maxUsers = plan.max_users_per_studio;
    const isUnlimitedUsers = maxUsers === 0;

    if (isUnlimitedUsers) {
      return {
        canAddUser: true,
        remainingUsers: Infinity,
        isAtUserLimit: false,
        userLimitPercent: 0,
      };
    }

    const canAddUser = currentUserCount < maxUsers;
    const remainingUsers = Math.max(0, maxUsers - currentUserCount);
    const isAtUserLimit = currentUserCount >= maxUsers;
    const userLimitPercent = Math.min(100, (currentUserCount / maxUsers) * 100);

    return {
      canAddUser,
      remainingUsers,
      isAtUserLimit,
      userLimitPercent,
    };
  }, [subscription, currentUserCount]);
};

/**
 * Hook for checking if user is approaching limits (80% threshold by default)
 *
 * @param warningThreshold - Percentage threshold for showing warnings (default: 80)
 * @returns Object with warning states
 */
export const useSubscriptionWarnings = (warningThreshold: number = 80) => {
  const { data: subscription } = useGetCurrentSubscriptionQuery();
  const { data: usage } = useGetSubscriptionUsageQuery();

  return useMemo(() => {
    if (!subscription || !usage) {
      return {
        shouldWarnStudioLimit: false,
        studioLimitPercent: 0,
      };
    }

    const { plan } = subscription;
    const maxStudios = plan.max_owned_studios;
    const currentStudios = usage.usage?.owned_studios?.count || 0;

    if (maxStudios === 0) {
      // Unlimited plan
      return {
        shouldWarnStudioLimit: false,
        studioLimitPercent: 0,
      };
    }

    const studioLimitPercent = (currentStudios / maxStudios) * 100;
    const shouldWarnStudioLimit = studioLimitPercent >= warningThreshold;

    return {
      shouldWarnStudioLimit,
      studioLimitPercent,
    };
  }, [subscription, usage, warningThreshold]);
};
